// app/frontend/pages/Auth/EditAccount.tsx
import { useState } from "react";
import { useForm } from "@mantine/form";
import { Stack, Anchor, Divider, Group, Modal, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconAlertCircle, IconTrash } from "@tabler/icons-react";
import { L2KH1, L2KH3, L2KText } from "../../components/ui/Typography";
import { L2KCard } from "../../components/ui/Cards";
import {
  L2KInput,
  L2KPasswordInput,
  L2KButton,
} from "../../components/ui/Forms";
import AuthFormLayout from "../../components/layouts/AuthFormLayout";
import { SEO } from "../../components/common/SEO";

interface User {
  id: number;
  email: string;
  name: string;
}

interface Props {
  user: User;
}

export default function EditAccount({ user }: Props) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] =
    useDisclosure(false);

  const profileForm = useForm({
    initialValues: {
      name: user.name || "",
      email: user.email || "",
      current_password: "",
    },
    validate: {
      name: (value) => (value.length < 2 ? "Name is too short" : null),
      email: (value) => (/^\S+@\S+$/.test(value) ? null : "Invalid email"),
      current_password: (value) =>
        value.length < 1 ? "Current password is required to save changes" : null,
    },
  });

  const passwordForm = useForm({
    initialValues: {
      current_password: "",
      password: "",
      password_confirmation: "",
    },
    validate: {
      current_password: (value) =>
        value.length < 1 ? "Current password is required" : null,
      password: (value) =>
        value.length < 6 ? "Password must be at least 6 characters" : null,
      password_confirmation: (value, values) =>
        value !== values.password ? "Passwords do not match" : null,
    },
  });

  const handleProfileUpdate = async (values: typeof profileForm.values) => {
    setIsUpdating(true);
    const csrfToken = document.querySelector<HTMLMetaElement>(
      'meta[name="csrf-token"]'
    )?.content;

    try {
      const formData = new FormData();
      formData.append("user[name]", values.name);
      formData.append("user[email]", values.email);
      formData.append("user[current_password]", values.current_password);

      const response = await fetch("/", {
        method: "PUT",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        credentials: "include",
        body: formData,
      });

      if (response.ok) {
        notifications.show({
          color: "green",
          title: "Profile Updated",
          message: "Your profile has been updated successfully.",
          icon: <IconCheck />,
          autoClose: 3000,
        });
        profileForm.setFieldValue("current_password", "");
      } else {
        const data = await response.json().catch(() => ({}));
        const errors = data.errors || {};

        notifications.show({
          color: "red",
          title: "Update Failed",
          message:
            Object.values(errors).flat().join(", ") ||
            "Could not update profile. Please check your password.",
          icon: <IconAlertCircle />,
          autoClose: 5000,
        });

        const formErrors: Record<string, string> = {};
        if (errors.email) formErrors.email = errors.email.join(", ");
        if (errors.name) formErrors.name = errors.name.join(", ");
        if (errors.current_password)
          formErrors.current_password = errors.current_password.join(", ");
        profileForm.setErrors(formErrors);
      }
    } catch (error) {
      notifications.show({
        color: "red",
        title: "Error",
        message: "An error occurred. Please try again.",
        icon: <IconAlertCircle />,
        autoClose: 5000,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePasswordChange = async (values: typeof passwordForm.values) => {
    setIsUpdating(true);
    const csrfToken = document.querySelector<HTMLMetaElement>(
      'meta[name="csrf-token"]'
    )?.content;

    try {
      const formData = new FormData();
      formData.append("user[current_password]", values.current_password);
      formData.append("user[password]", values.password);
      formData.append("user[password_confirmation]", values.password_confirmation);

      const response = await fetch("/", {
        method: "PUT",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        credentials: "include",
        body: formData,
      });

      if (response.ok) {
        notifications.show({
          color: "green",
          title: "Password Changed",
          message: "Your password has been updated successfully.",
          icon: <IconCheck />,
          autoClose: 3000,
        });
        passwordForm.reset();
      } else {
        const data = await response.json().catch(() => ({}));
        const errors = data.errors || {};

        notifications.show({
          color: "red",
          title: "Password Change Failed",
          message:
            Object.values(errors).flat().join(", ") ||
            "Could not change password. Please check your current password.",
          icon: <IconAlertCircle />,
          autoClose: 5000,
        });

        const formErrors: Record<string, string> = {};
        if (errors.current_password)
          formErrors.current_password = errors.current_password.join(", ");
        if (errors.password) formErrors.password = errors.password.join(", ");
        passwordForm.setErrors(formErrors);
      }
    } catch (error) {
      notifications.show({
        color: "red",
        title: "Error",
        message: "An error occurred. Please try again.",
        icon: <IconAlertCircle />,
        autoClose: 5000,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    const csrfToken = document.querySelector<HTMLMetaElement>(
      'meta[name="csrf-token"]'
    )?.content;

    try {
      const response = await fetch("/", {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        credentials: "include",
      });

      if (response.ok) {
        notifications.show({
          color: "green",
          title: "Account Deleted",
          message: "Your account has been deleted. Goodbye!",
          icon: <IconCheck />,
          autoClose: 3000,
        });
        setTimeout(() => {
          window.location.href = "/";
        }, 1500);
      } else {
        notifications.show({
          color: "red",
          title: "Error",
          message: "Could not delete account. Please try again.",
          icon: <IconAlertCircle />,
          autoClose: 5000,
        });
      }
    } catch (error) {
      notifications.show({
        color: "red",
        title: "Error",
        message: "An error occurred. Please try again.",
        icon: <IconAlertCircle />,
        autoClose: 5000,
      });
    } finally {
      setIsDeleting(false);
      closeDeleteModal();
    }
  };

  return (
    <AuthFormLayout maxWidth={500}>
      <SEO title="Account Settings - Learntokiz" />
      <Stack gap="lg">
        <Stack gap="xs" ta="center">
          <L2KH1 variant="accent">Account Settings</L2KH1>
          <L2KText variant="secondary">
            Manage your profile and security settings.
          </L2KText>
        </Stack>

        {/* Profile Section */}
        <L2KCard variant="elevated" glow>
          <form onSubmit={profileForm.onSubmit(handleProfileUpdate)}>
            <Stack gap="md">
              <L2KH3>Profile</L2KH3>
              <L2KInput
                label="Name"
                placeholder="Your name"
                required
                {...profileForm.getInputProps("name")}
              />
              <L2KInput
                label="Email"
                placeholder="you@example.com"
                required
                {...profileForm.getInputProps("email")}
              />
              <Divider my="xs" color="rgba(255,255,255,0.1)" />
              <L2KPasswordInput
                label="Current Password"
                placeholder="Required to save changes"
                required
                {...profileForm.getInputProps("current_password")}
              />
              <L2KButton
                fullWidth
                size="md"
                type="submit"
                loading={isUpdating}
              >
                Save Profile
              </L2KButton>
            </Stack>
          </form>
        </L2KCard>

        {/* Password Section */}
        <L2KCard variant="elevated">
          <form onSubmit={passwordForm.onSubmit(handlePasswordChange)}>
            <Stack gap="md">
              <L2KH3>Change Password</L2KH3>
              <L2KPasswordInput
                label="Current Password"
                placeholder="Your current password"
                required
                {...passwordForm.getInputProps("current_password")}
              />
              <L2KPasswordInput
                label="New Password"
                placeholder="At least 6 characters"
                required
                {...passwordForm.getInputProps("password")}
              />
              <L2KPasswordInput
                label="Confirm New Password"
                placeholder="Confirm your new password"
                required
                {...passwordForm.getInputProps("password_confirmation")}
              />
              <L2KButton
                fullWidth
                size="md"
                type="submit"
                loading={isUpdating}
                variant="outline"
              >
                Change Password
              </L2KButton>
            </Stack>
          </form>
        </L2KCard>

        {/* Danger Zone */}
        <L2KCard variant="elevated">
          <Stack gap="md">
            <L2KH3 style={{ color: "#ef4444" }}>Danger Zone</L2KH3>
            <L2KText variant="secondary" size="sm">
              Once you delete your account, there is no going back. Please be
              certain.
            </L2KText>
            <L2KButton
              variant="outline"
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={openDeleteModal}
            >
              Delete Account
            </L2KButton>
          </Stack>
        </L2KCard>

        <L2KText size="sm" ta="center">
          <Anchor href="/dashboard" style={{ color: "#FF9800" }}>
            Back to Dashboard
          </Anchor>
        </L2KText>
      </Stack>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        title="Delete Account"
        centered
        styles={{
          header: { backgroundColor: "#1a1a1a", borderBottom: "1px solid #333" },
          content: { backgroundColor: "#1a1a1a" },
          title: { color: "#ef4444", fontWeight: 600 },
        }}
      >
        <Stack gap="md">
          <Text c="dimmed">
            Are you sure you want to delete your account? This action cannot be
            undone and all your data will be permanently removed.
          </Text>
          <Group justify="flex-end" gap="sm">
            <L2KButton variant="subtle" onClick={closeDeleteModal}>
              Cancel
            </L2KButton>
            <L2KButton
              color="red"
              loading={isDeleting}
              onClick={handleDeleteAccount}
            >
              Delete Account
            </L2KButton>
          </Group>
        </Stack>
      </Modal>
    </AuthFormLayout>
  );
}
